/** D6 - the battle kernel's struct-of-arrays stack store.
 *
 * Parallel typed arrays indexed by stack slot; `StackId` resolves to a slot once
 * at battle start. Iteration is `for (let i = 0; i < n; i++)`, so I3 is
 * satisfied by construction and the in-place undo journal (D1) is a typed-array
 * write.
 *
 * Widths are Int32 unless a spec proves narrower is safe.
 */

import type { ArmyId, CreatureDefId, HeroId, PlayerId, SpellDefId } from "../ids.js";
import type { Modifier } from "../modifiers.js";
import type { RngStreamState } from "../rng/pcg32.js";

/** The battlefield is 15 columns by 11 rows of hexes, offset every other row. */
export const BF_WIDTH = 15;
export const BF_HEIGHT = 11;
export const BF_HEXES = BF_WIDTH * BF_HEIGHT;

export const SIDE_ATTACKER = 0;
export const SIDE_DEFENDER = 1;

/** Bit flags on `flags`, one Int32 per stack. */
export const F_ALIVE = 1 << 0;
export const F_MOVED = 1 << 1;
export const F_WAITED = 1 << 2;
export const F_DEFENDING = 1 << 3;
export const F_RETALIATED = 1 << 4;
export const F_FLYING = 1 << 5;
export const F_SHOOTER = 1 << 6;
export const F_TWO_HEX = 1 << 7;
export const F_UNDEAD = 1 << 8;
export const F_NO_MELEE_PENALTY = 1 << 9;
export const F_NO_RETALIATION = 1 << 10;
export const F_BLINDED = 1 << 11;
export const F_PETRIFIED = 1 << 12;
export const F_BERSERK = 1 << 13;
export const F_TELEPORTS = 1 << 14;
export const F_WAR_MACHINE = 1 << 15;
export const F_SIEGE_WALL = 1 << 16;
/**
 * War-machine roles. Bits 17..23 belong to `flags.ts`, so the content-derived
 * machine bits start at 24; see `machines.ts` for how a role is read off the
 * def. Exactly one of these is ever set, and only alongside F_WAR_MACHINE.
 */
export const F_MACHINE_BREAK = 1 << 24;
export const F_MACHINE_HEAL = 1 << 25;
export const F_MACHINE_AMMO = 1 << 26;
/**
 * Nothing alive in there: an elemental, a sentinel, a rookstone, an engine. Set off
 * the NON_LIVING ability, so it is content-derived and belongs here rather than
 * in `flags.ts`, which owns 17..23 and 27..28 - 29 is the next free bit. Read
 * with F_UNDEAD by `isMindImmune`: the two are the mindless between them.
 */
export const F_NON_LIVING = 1 << 29;
/** Blackwater pack: School, Flop, wet-tile luck. See `wet.ts`. */
export const F_SCHOOL = 1 << 30;

export interface StackStore {
  readonly n: number;
  /** Content def for each slot; parallel to the typed arrays. */
  readonly def: readonly CreatureDefId[];
  readonly side: Int32Array;
  readonly slotInArmy: Int32Array;
  readonly count: Int32Array;
  /** HP remaining on the top (partially wounded) creature. */
  readonly topHp: Int32Array;
  readonly maxHp: Int32Array;
  readonly hex: Int32Array;
  readonly attack: Int32Array;
  readonly defense: Int32Array;
  readonly speed: Int32Array;
  readonly damageMin: Int32Array;
  readonly damageMax: Int32Array;
  readonly shots: Int32Array;
  readonly casts: Int32Array;
  /**
   * Magic strikes left this battle, per stack - `MAGIC_ATTACK`'s budget.
   *
   * A column of its own rather than a second meaning for `casts`, because a
   * creature may carry both and "spells I may cast" and "blows I may charge"
   * are not one pool. Sharing them would read fine on the Thunder Colossus, which does
   * not cast, and become a bug the first time somebody gives a caster a
   * charged blow.
   */
  readonly strikes: Int32Array;
  readonly morale: Int32Array;
  readonly luck: Int32Array;
  readonly flags: Int32Array;
  /**
   * The second flag word. `flags` ran out: bits 0..30 are spoken for and 31 is
   * the sign bit of an Int32, so the next bookkeeping bit had nowhere to go.
   * Everything minted in `flags.ts` as `F2_*` lives here; see that file for why
   * this is a second column rather than three more bits squeezed into the first.
   */
  readonly flags2: Int32Array;
  /**
   * Rounds until an ACTIVATED ability may be used again, per stack. Zero means
   * ready. Ticked down at each round start, so it is a countdown in rounds and
   * not a stamp of the round it was used - a stamp would have to be compared
   * against a round number that a loaded save can start from anywhere.
   */
  readonly cooldown: Int32Array;
  /** Facing: 1 for right, -1 for left. Two-hex stacks occupy hex and hex-facing. */
  readonly facing: Int32Array;
  /** Turn-scoped stat modifiers, per slot. */
  readonly mods: readonly (readonly Modifier[])[];
  /** Kills accumulated this battle, for the after-action report. */
  readonly killed: Int32Array;
}

export interface BattleHeroState {
  readonly id: HeroId | null;
  readonly owner: PlayerId | null;
  readonly attack: number;
  readonly defense: number;
  readonly spellPower: number;
  readonly knowledge: number;
  readonly mana: number;
  readonly spellbook: readonly SpellDefId[];
  readonly castThisRound: boolean;
  /**
   * The side's faction racial has been spent. One charge for the whole battle,
   * which is what keeps a racial a decision rather than a rotation: see
   * `battle/racial.ts`.
   */
  readonly racialUsed: boolean;
  /**
   * What the hero has taken swinging, this battle.
   *
   * He is not on the field and nothing can aim at him. The only way this number
   * moves is `BATTLE_HERO_STRIKE`: the hero leans into the line, and a stack
   * with a retaliation left leans back. It is the price on a verb that was
   * otherwise free damage - measured over 36 battles when the strike shipped,
   * the AI took it in every single one - and it makes "which stack do I hit"
   * a question with an answer, because a stack that has already retaliated this
   * round cannot answer at all.
   *
   * At or past `heroStamina` the hero goes down and the battle ends as a
   * `retreat` for his side: he lives, his army does not. That is the same
   * outcome the retreat command produces, on purpose - a knockout is a retreat
   * the enemy chose for you, and every consequence of one is already written.
   */
  readonly wounds: number;
  readonly army: ArmyId | null;
}

export type ObstacleKind =
  | "rock" | "tree" | "moat" | "wall" | "gate" | "tower"
  | "sinking_sand" | "landmine" | "firewall" | "forcefield"
  // Ground that costs something to cross rather than refusing to be crossed.
  // Rolled by `field.ts` onto an open field, symmetric by construction: one
  // board is laid before either side moves and both armies fight over it.
  | "mud" | "embers";

export interface Obstacle {
  readonly kind: ObstacleKind;
  readonly hex: number;
  /**
   * How much of the obstacle is left.
   *
   * For masonry that is structural: 0 destroyed, 1 damaged, 2 intact. For an
   * obstacle a spell conjured it is a countdown in rounds, ticked at the top of
   * each round and removed at zero. The two readings are the same sentence -
   * "how much of this is still here" - which is why one field carries both and
   * no save format had to grow a second.
   */
  readonly integrity: number;
  /**
   * How hard a conjured obstacle bites: the damage a Rune Trap or a Flame Barrier
   * deals to whoever finds it. Absent on masonry, which bites nobody, and
   * optional in the save format for the same reason - an obstacle written
   * before spells could conjure one has no answer and needs none.
   */
  readonly power?: number;
  readonly art: string;
}

/** Obstacles a spell put there, which tick down and can be dispelled. */
export const CONJURED_OBSTACLES: readonly ObstacleKind[] =
  Object.freeze(["sinking_sand", "landmine", "firewall", "forcefield"]);

export const isConjured = (o: Obstacle): boolean =>
  CONJURED_OBSTACLES.includes(o.kind);

export type BattleOutcome =
  | { readonly kind: "ongoing" }
  | { readonly kind: "victory"; readonly side: 0 | 1 }
  | { readonly kind: "retreat"; readonly side: 0 | 1 }
  | { readonly kind: "surrender"; readonly side: 0 | 1 }
  | { readonly kind: "draw" };

export interface BattleState {
  readonly round: number;
  /** Index into `order`; whose stack acts now. */
  readonly turnIndex: number;
  /** Slot order for this round, recomputed at each round start. */
  readonly order: Int32Array;
  readonly stacks: StackStore;
  readonly heroes: readonly [BattleHeroState, BattleHeroState];
  readonly obstacles: readonly Obstacle[];
  readonly terrain: string;
  readonly siege: {
    readonly active: boolean;
    readonly fortLevel: number;
    readonly gateOpen: boolean;
  } | null;
  readonly rng: Readonly<Record<string, RngStreamState>>;
  readonly outcome: BattleOutcome;
  /** Set when a stack is waiting to act after having used WAIT. */
  readonly waited: Int32Array;
  readonly tacticsPhase: boolean;
  /**
   * The tactics band, signed. The classic does not give both armies a tactics phase: the
   * side with the better Tactics rearranges and the other watches, by the
   * *difference* between the two. One signed integer says both halves of that -
   * magnitude is the band's width in columns, sign is who won - so the rule
   * needs no second field, no migration, and no way to be in the state where a
   * width and a side disagree. Positive is the attacker's, negative the
   * defender's, zero no phase at all.
   */
  readonly tacticsHexes: number;
  /**
   * One byte per hex, 1 if that cell is wet. Absent on a dry board so a grass
   * fight does not grow a 165-byte zero array and does not change its hash.
   * Swamp, river and boat battles write it; a Leviathan can flood more mid-fight.
   */
  readonly wet?: Uint8Array;
  /** Emberpit Cinders. Absent on a board that was never on fire. */
  readonly fire?: Uint8Array;
  /** Fenmire Miasma. Absent on a board that was never poisoned. */
  readonly poison?: Uint8Array;
}

// --------------------------------------------------------------- hex helpers

/** Whose phase it is. Meaningless while `tacticsPhase` is false. */
export const tacticsSide = (s: BattleState): 0 | 1 =>
  (s.tacticsHexes < 0 ? SIDE_DEFENDER : SIDE_ATTACKER) as 0 | 1;

/** How many columns deep that side may rearrange in. Never negative. */
export const tacticsBand = (s: BattleState): number =>
  s.tacticsHexes < 0 ? -s.tacticsHexes : s.tacticsHexes;

/** The column band a side's tactics moves must land in. */
export function inTacticsBand(s: BattleState, side: number, col: number): boolean {
  if (side !== tacticsSide(s)) return false;
  const band = tacticsBand(s);
  return side === SIDE_ATTACKER ? col < band : col >= BF_WIDTH - band;
}

export const hexIndex = (col: number, row: number): number => row * BF_WIDTH + col;
export const hexCol = (h: number): number => h % BF_WIDTH;
export const hexRow = (h: number): number => (h / BF_WIDTH) | 0;

/**
 * Six neighbours of an odd-row-offset hex grid, in a fixed clockwise order
 * starting from the top-left. Order is part of the contract: pathfinding
 * tie-breaks on it, so changing it changes replays.
 */
export function hexNeighbours(h: number, out: number[]): number {
  const col = hexCol(h);
  const row = hexRow(h);
  const odd = (row & 1) === 1;
  const dx = odd ? 0 : -1;
  const cand: readonly (readonly [number, number])[] = [
    [col + dx, row - 1],
    [col + dx + 1, row - 1],
    [col + 1, row],
    [col + dx + 1, row + 1],
    [col + dx, row + 1],
    [col - 1, row],
  ];
  let n = 0;
  for (const [c, r] of cand) {
    if (c < 0 || r < 0 || c >= BF_WIDTH || r >= BF_HEIGHT) continue;
    out[n++] = hexIndex(c, r);
  }
  return n;
}

/** Axial distance between two hexes on the offset grid. */
export function hexDistance(a: number, b: number): number {
  const ac = hexCol(a), ar = hexRow(a);
  const bc = hexCol(b), br = hexRow(b);
  const aq = ac - ((ar - (ar & 1)) >> 1);
  const bq = bc - ((br - (br & 1)) >> 1);
  const as = -aq - ar;
  const bs = -bq - br;
  return (Math.abs(aq - bq) + Math.abs(ar - br) + Math.abs(as - bs)) >> 1;
}

export const hasFlag = (s: StackStore, i: number, f: number): boolean => (s.flags[i]! & f) !== 0;
export const hasFlag2 = (s: StackStore, i: number, f: number): boolean => (s.flags2[i]! & f) !== 0;
export const isAlive = (s: StackStore, i: number): boolean => (s.flags[i]! & F_ALIVE) !== 0 && s.count[i]! > 0;
